import React, { useMemo, useState } from "react";
import axios from "axios";
import Select from "react-select";
import {
  FaCheckCircle,
  FaExclamationCircle,
  FaExclamationTriangle,
  FaGlobe,
  FaRobot,
  FaSitemap,
  FaTimesCircle,
} from "react-icons/fa";
import { FiCopy, FiExternalLink, FiSearch, FiZap } from "react-icons/fi";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";

const API_BASE_URL = (process.env.REACT_APP_API_URL || "https://tools.microters.com").replace(
  /\/$/,
  ""
);

const USER_AGENT_OPTIONS = [
  { label: "Googlebot Smartphone", value: "Googlebot" },
  { label: "Googlebot Desktop", value: "Googlebot" },
  { label: "Bingbot", value: "bingbot" },
  { label: "Bingbot Mobile", value: "bingbot" },
  { label: "GPTBot", value: "GPTBot" },
  { label: "ChatGPT-User", value: "ChatGPT-User" },
  { label: "ClaudeBot", value: "ClaudeBot" },
  { label: "PerplexityBot", value: "PerplexityBot" },
  { label: "Generic crawler (*)", value: "*" },
];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "40px",
    border: "none",
    borderRadius: "4px",
    backgroundColor: "transparent",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(255, 88, 54, 0.12)" : "none",
    cursor: "pointer",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 12px" }),
  singleValue: (base) => ({ ...base, color: "#26324a", fontSize: "14px" }),
  placeholder: (base) => ({ ...base, color: "#8f98aa", fontSize: "14px" }),
  indicatorSeparator: (base) => ({ ...base, backgroundColor: "#d9dee7" }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused ? "#ff5836" : "#b3bac6",
    "&:hover": { color: "#ff5836" },
  }),
  menu: (base) => ({
    ...base,
    zIndex: 30,
    overflow: "hidden",
    border: "1px solid #e3e7ee",
    borderRadius: "10px",
    boxShadow: "0 16px 35px rgba(31, 43, 70, 0.14)",
  }),
  option: (base, state) => ({
    ...base,
    padding: "10px 12px",
    color: state.isSelected ? "#ffffff" : "#273149",
    backgroundColor: state.isSelected
      ? "#ff5836"
      : state.isFocused
        ? "#fff0eb"
        : "#ffffff",
    cursor: "pointer",
  }),
};

const formatBytes = (bytes) => {
  const value = Number(bytes) || 0;
  if (value < 1024) return `${value} B`;
  return `${(value / 1024).toFixed(1)} KB`;
};

const StatusIcon = ({ type = "success" }) => {
  if (type === "error") return <FaTimesCircle aria-hidden="true" />;
  if (type === "warning") return <FaExclamationTriangle aria-hidden="true" />;
  return <FaCheckCircle aria-hidden="true" />;
};

const RobotsTxtValidator = () => {
  const [url, setUrl] = useState("");
  const [selectedAgent, setSelectedAgent] = useState(USER_AGENT_OPTIONS[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [showRaw, setShowRaw] = useState(false);

  const status = useMemo(() => {
    if (!result) return null;

    if (!result.found || !result.valid) {
      return {
        type: "error",
        label: result.found ? "Needs fixes" : "Not found",
      };
    }

    if (result.warnings?.length) {
      return { type: "warning", label: "Valid with warnings" };
    }

    return { type: "success", label: "Valid robots.txt" };
  }, [result]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!url.trim()) {
      setError("Please enter a website URL.");
      toast.error("Please enter a website URL.");
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);
    setShowRaw(false);

    console.log("[ROBOTS VALIDATOR UI] Sending request:", {
      url: url.trim(),
      userAgent: selectedAgent.value,
    });

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/robots-validator`,
        {
          url: url.trim(),
          userAgent: selectedAgent.value,
        },
        { timeout: 20000 }
      );

      if (!response.data?.success || !response.data?.data) {
        throw new Error(response.data?.message || "The validator returned an invalid response.");
      }

      console.log("[ROBOTS VALIDATOR UI] Result received:", response.data.data);
      setResult(response.data.data);
      toast.success("robots.txt test completed.");
    } catch (requestError) {
      const message =
        requestError.response?.data?.message ||
        requestError.message ||
        "Unable to validate robots.txt.";

      console.error("[ROBOTS VALIDATOR UI] Request failed:", {
        message,
        status: requestError.response?.status,
        data: requestError.response?.data,
      });

      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const copyRobotsContent = async () => {
    try {
      await navigator.clipboard.writeText(result?.content || "");
      toast.success("robots.txt content copied.");
    } catch (copyError) {
      console.error("[ROBOTS VALIDATOR UI] Copy failed:", copyError);
      toast.error("Could not copy the robots.txt content.");
    }
  };

  return (
    <div className="rtv-page">
      <ToastContainer position="top-right" autoClose={2500} />

      <div className="rtv-shell">
       

        <section className="rtv-tool-card">
          <form className="rtv-form-panel" onSubmit={handleSubmit} noValidate>
            <h2>
              <FaRobot aria-hidden="true" /> Configure Test
            </h2>

            <div className={`rtv-field ${error && !url.trim() ? "rtv-field-error" : ""}`}>
              <label htmlFor="robotsWebsiteUrl">
                <FaGlobe aria-hidden="true" /> Website URL <span>*</span>
              </label>
              <input
                id="robotsWebsiteUrl"
                type="text"
                inputMode="url"
                value={url}
                onChange={(event) => {
                  setUrl(event.target.value);
                  if (error) setError("");
                }}
                placeholder="https://example.com"
                autoComplete="url"
                disabled={loading}
              />
            </div>

            <div className="rtv-field rtv-select-field">
              <label htmlFor="robotsUserAgent">
                <FiSearch aria-hidden="true" /> User Agent
              </label>
              <Select
                inputId="robotsUserAgent"
                options={USER_AGENT_OPTIONS}
                value={selectedAgent}
                onChange={(option) => option && setSelectedAgent(option)}
                styles={selectStyles}
                isSearchable
                isDisabled={loading}
              />
            </div>

            {error && <div className="rtv-inline-error">{error}</div>}

            <button className="rtv-test-button" type="submit" disabled={loading}>
              <FiZap aria-hidden="true" />
              {loading ? "Testing robots.txt..." : "Test Robots.txt"}
            </button>

            <div className="rtv-notice">
              <FaExclamationTriangle aria-hidden="true" />
              <p>
                Ensure the URL includes <strong>http://</strong> or <strong>https://</strong>. This
                tool fetches the live robots.txt file.
              </p>
            </div>
          </form>

          <div className="rtv-results-panel">
            <div className="rtv-results-title-row">
              <h2>Validation Results</h2>
              {status && (
                <span className={`rtv-status rtv-status-${status.type}`}>
                  <StatusIcon type={status.type} /> {status.label}
                </span>
              )}
            </div>

            {!result && !loading && (
              <div className="rtv-empty-state">
                <FaRobot aria-hidden="true" />
                <p>Enter a URL and click Test to see results.</p>
              </div>
            )}

            {loading && (
              <div className="rtv-empty-state rtv-loading-state">
                <span className="rtv-spinner" aria-hidden="true" />
                <p>Fetching and validating the live robots.txt file...</p>
              </div>
            )}

            {result && (
              <div className="rtv-result-content">
                <div className="rtv-decision-card">
                  <div
                    className={`rtv-decision-icon ${
                      result.crawlAllowed === true
                        ? "is-allowed"
                        : result.crawlAllowed === false
                          ? "is-blocked"
                          : "is-unknown"
                    }`}
                  >
                    {result.crawlAllowed === true ? (
                      <FaCheckCircle />
                    ) : result.crawlAllowed === false ? (
                      <FaTimesCircle />
                    ) : (
                      <FaExclamationCircle />
                    )}
                  </div>
                  <div>
                    <span>Root crawl test for {result.userAgent}</span>
                    <strong>
                      {result.crawlAllowed === true
                        ? "Crawling allowed"
                        : result.crawlAllowed === false
                          ? "Crawling blocked"
                          : "Permission unknown"}
                    </strong>
                    <p>{result.decisionReason}</p>
                  </div>
                </div>

                <div className="rtv-metrics">
                  <div>
                    <span>HTTP</span>
                    <strong>{result.httpStatus}</strong>
                  </div>
                  <div>
                    <span>Groups</span>
                    <strong>{result.stats?.groups || 0}</strong>
                  </div>
                  <div>
                    <span>Rules</span>
                    <strong>{result.stats?.rules || 0}</strong>
                  </div>
                  <div>
                    <span>Size</span>
                    <strong>{formatBytes(result.bytes)}</strong>
                  </div>
                </div>

                <div className="rtv-result-list">
                  <div className="rtv-result-line">
                    <span>robots.txt URL</span>
                    <a href={result.finalUrl} target="_blank" rel="noreferrer">
                      Open file <FiExternalLink />
                    </a>
                  </div>
                  <div className="rtv-result-line">
                    <span>Matched group</span>
                    <strong>{result.matchedUserAgents?.join(", ") || "None"}</strong>
                  </div>
                  <div className="rtv-result-line">
                    <span>Matched rule</span>
                    <strong>
                      {result.matchedRule
                        ? `${result.matchedRule.type}: ${result.matchedRule.path || "(empty)"}`
                        : "No matching rule"}
                    </strong>
                  </div>
                </div>

                {result.errors?.length > 0 && (
                  <div className="rtv-issues rtv-issues-error">
                    <h3>
                      <FaTimesCircle /> Errors ({result.errors.length})
                    </h3>
                    <ul>
                      {result.errors.map((item, index) => (
                        <li key={`error-${item.line || "general"}-${index}`}>
                          {item.line ? `Line ${item.line}: ` : ""}
                          {item.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {result.warnings?.length > 0 && (
                  <div className="rtv-issues rtv-issues-warning">
                    <h3>
                      <FaExclamationTriangle /> Warnings ({result.warnings.length})
                    </h3>
                    <ul>
                      {result.warnings.map((item, index) => (
                        <li key={`warning-${item.line || "general"}-${index}`}>
                          {item.line ? `Line ${item.line}: ` : ""}
                          {item.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {result.sitemaps?.length > 0 && (
                  <div className="rtv-sitemaps">
                    <h3>
                      <FaSitemap /> Sitemaps ({result.sitemaps.length})
                    </h3>
                    {result.sitemaps.map((sitemap) => (
                      <a key={sitemap} href={sitemap} target="_blank" rel="noreferrer">
                        {sitemap} <FiExternalLink />
                      </a>
                    ))}
                  </div>
                )}

                {result.found && (
                  <div className="rtv-raw-section">
                    <div className="rtv-raw-actions">
                      <button type="button" onClick={() => setShowRaw((current) => !current)}>
                        {showRaw ? "Hide raw robots.txt" : "View raw robots.txt"}
                      </button>
                      <button type="button" onClick={copyRobotsContent}>
                        <FiCopy /> Copy
                      </button>
                    </div>
                    {showRaw && <pre>{result.content}</pre>}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default RobotsTxtValidator;
